import React, { lazy, useContext, useState } from 'react'
import { graphql } from 'gatsby'
import { DefaultHead } from '../components/DefaultHead'
import { WebcamLayout } from './webcam/src/WebcamLayout'
import { WebcamLanding } from './webcam/src/landing/WebcamLanding'
import { LandingHero } from '../components/LandingHero'
import { MessagesContext } from '../common/intl/MessagesContext'
import { useWebcamTestContextValue, WebcamTestContext } from './webcam/src/WebcamTestContext'
import { ExtendedSuspense } from '../common/components/ExtendedSuspense'
import { allLocales } from '../common/intl/Locale'

const WebcamTest = lazy(() => import('./webcam/src/WebcamTest'))

export default function Page() {
  const { webcam } = useContext(MessagesContext)
  const [testLoaded, setTestLoaded] = useState(false)
  const testContextValue = useWebcamTestContextValue()
  const { autostart, setAutostart, testEnabled, setTestEnabled } = testContextValue
  const testReady = testEnabled && testLoaded

  return (
    <WebcamLayout locales={allLocales}>
      <WebcamTestContext.Provider value={testContextValue}>
        {testEnabled && (
          <ExtendedSuspense onLoaded={() => setTestLoaded(true)}>
            <WebcamTest />
          </ExtendedSuspense>
        )}

        {testReady || (
          <>
            <LandingHero
              title={webcam?.hero?.title}
              description={webcam?.hero?.description}
              startLoading={testEnabled && !testLoaded}
              onStartCLick={() => {
                setTestEnabled(true)
              }}
              autostart={autostart}
              setAutostart={setAutostart}
            />

            <WebcamLanding />
          </>
        )}
      </WebcamTestContext.Provider>
    </WebcamLayout>
  )
}

export const Head = DefaultHead

export const query = graphql`
  fragment TestMessages on Messages {
    test {
      loading
      hero {
        start
        try
        autostart
      }
      toolbar {
        exit
        settings
      }
      info {
        title
      }
      access {
        how
        change
        again
        browser
        unknownError {
          title
          description
        }
        error {
          type
          title
          description
        }
      }
    }
  }

  query Webcam($locale: String) {
    messages(locale: { eq: $locale }) {
      ...AppLayoutMessages
      ...TestMessages
      webcam {
        test {
          record {
            download
            start
            stop
            pause
            resume
            play
          }
          devices {
            title
          }
          settings {
            webcam
            mic
            speakers
          }
          access {
            both {
              title
              description
            }
            single {
              title
              description
            }
          }
          view {
            title
            mirror
            human
          }
          grid {
            title
            center
            circle
          }
          filters {
            title
            description
            brightness
            contrast
            saturate
            sepia
            grayscale
            hue
            invert
            blur
            reset
          }
        }
        hero {
          title
          description
        }
        features {
          title
          items {
            title
            description
          }
        }
        why {
          title
          items {
            title
            description
          }
        }
        how {
          title
          items {
            title
            description
          }
        }
      }
    }
  }
`
